import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';

import { scaffoldNewAccount, sendPasswordReset, signInWithEmail, signInWithGoogle } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { AccountSetupProblem } from '@/components/AccountSetupProblem';
import { AuthLayout, Divider } from '@/components/AuthLayout';
import { AuthConsentNotice } from '@/components/AuthConsentNotice';
import { InviteAuthHint } from '@/components/InviteAuthHint';
import { useAuth } from '@/hooks/useAuth';
import { auth as firebaseAuth } from '@/lib/firebase';

const FRIENDLY: Record<string, string> = {
  'auth/wrong-password': 'Email or password is wrong.',
  'auth/invalid-credential': 'Email or password is wrong.',
  'auth/user-not-found': "We don't have an account for that email.",
  'auth/too-many-requests': 'Too many attempts — try again later.',
};

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  // Arriving from an invite link → steer to Google (pre-verified, skips the
  // email-verification step accept_invite requires).
  const isInvite = next.startsWith('/invites/');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  // A NEW Google user can arrive via /login (the invite flow steers
  // "Continue with Google" here). signInWithGoogle then scaffolds their
  // account — the same two-step flow as Signup, with the same failure
  // mode: auth user created, account scaffold failed. Keep them here
  // with a retry instead of bouncing to the "Nothing to manage yet"
  // dead-end.
  const [scaffoldFailed, setScaffoldFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Mirror Signup's guard: don't navigate on the auth-state transition
  // mid-submit — signInWithPopup flips auth.state BEFORE the new-user
  // scaffold completes, and navigating then lands on a page with no
  // memberships that spins forever.
  useEffect(() => {
    if (auth.state === 'signedIn' && !busy && !scaffoldFailed) {
      navigate(next, { replace: true });
    }
  }, [auth.state, navigate, next, busy, scaffoldFailed]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithEmail(email, password);
      navigate(next, { replace: true });
    } catch (e) {
      const code = e instanceof FirebaseError ? e.code : null;
      setError((code && FRIENDLY[code]) || (e instanceof Error ? e.message : 'Sign in failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function onForgotPassword() {
    setError(null);
    setResetSent(false);
    if (!email) {
      setError('Enter your email above first, then click Forgot password.');
      return;
    }
    setResetBusy(true);
    try {
      await sendPasswordReset(email);
      setResetSent(true);
    } catch (e) {
      // Mirror the success-on-unknown-email behaviour for privacy:
      // only surface "real" errors to the user (network, etc).
      const code = e instanceof FirebaseError ? e.code : null;
      if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
        setResetSent(true);
      } else {
        setError(e instanceof Error ? e.message : 'Could not send reset email.');
      }
    } finally {
      setResetBusy(false);
    }
  }

  async function onGoogle() {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
      navigate(next, { replace: true });
    } catch (e) {
      // Signed in but the throw came after popup success ⇒ a new user
      // whose account scaffold failed — offer the retry (same as
      // Signup) instead of an error on a page they'd be navigated off.
      if (firebaseAuth.currentUser) {
        setScaffoldFailed(true);
      } else {
        setError(e instanceof Error ? e.message : 'Google sign-in failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function retryScaffold() {
    if (!firebaseAuth.currentUser) return;
    setRetrying(true);
    try {
      // Idempotent server-side (ensure_user_profile) — safe to re-run.
      await scaffoldNewAccount(firebaseAuth.currentUser, 'google');
      setScaffoldFailed(false);
      navigate(next, { replace: true });
    } catch {
      // Stay on the retry screen; the button re-enables for another attempt.
    } finally {
      setRetrying(false);
    }
  }

  const googleButton = (
    <Button variant={isInvite ? 'primary' : 'secondary'} onClick={onGoogle} disabled={busy}>
      Continue with Google
    </Button>
  );

  // Auth succeeded but the account never scaffolded — retry instead of a
  // dead-end. Reachable only after a caught scaffold failure (onGoogle).
  if (scaffoldFailed) {
    return (
      <AuthLayout title="Almost there">
        <AccountSetupProblem onRetry={retryScaffold} retrying={retrying} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Sign in">
      {isInvite && (
        <>
          <InviteAuthHint />
          {googleButton}
          <Divider label="or sign in with email" />
        </>
      )}
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <div className="-mt-2 text-right">
          <button
            type="button"
            className="text-xs text-accent-deep hover:underline disabled:opacity-50"
            onClick={() => void onForgotPassword()}
            disabled={resetBusy}
          >
            {resetBusy ? 'Sending…' : 'Forgot password?'}
          </button>
        </div>
        {resetSent && (
          <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
            If that email is on file, we've sent a reset link. Check your inbox (and spam).
          </div>
        )}
        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        <Button type="submit" variant={isInvite ? 'secondary' : 'primary'} disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
      {!isInvite && (
        <>
          <Divider />
          {googleButton}
        </>
      )}
      <AuthConsentNotice />
      <p className="mt-6 text-sm text-ink-muted">
        New here?{' '}
        <Link
          to={`/signup${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`}
          className="font-medium text-accent-deep hover:underline"
        >
          Create an account
        </Link>
      </p>
    </AuthLayout>
  );
}
